import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ENVIRONMENT } from '@ninety/mobile/platform/environments';

import { CreateRockRequestModel } from '../_state/models/rock-request.model';
import { RockModel } from '../_state/models/rock.model';
import { RocksAndMilestonesResponse } from '../_state/models/rocks-milestones-response.model';
import { RockStatusCode } from '../_state/models/status-code';

/** Query args for the paged rocks-and-milestones list. */
export interface RockListParams {
  teamId: string;
  sortDirection: string;
  currentUserId: string;
  rockStatus: RockStatusCode | string;
  showArchived: boolean;
  filterFutureCurrent: boolean;
  pageIndex: number;
  pageSize: number;
  userId: string;
}

/**
 * HTTP client for the Rocks backend. One method per controller endpoint; owns URL composition,
 * query-param building, and verbs. Every operation targets the nest service — the `RocksApiFlag`
 * that used to pick monolith-or-nest per operation is gone, along with the monolith routes it
 * could select.
 */
@Injectable({ providedIn: 'root' })
export class RocksApiService {
  private readonly http = inject(HttpClient);
  private readonly origin = inject(ENVIRONMENT).origin;

  private readonly nest = `${this.origin.rocksNest}/v1/rocks`;

  getRocksAndMilestones(params: RockListParams): Observable<RocksAndMilestonesResponse> {
    return this.http.get<RocksAndMilestonesResponse>(`${this.nest}/rocksAndMilestones`, {
      params: this.buildListParams(params),
    });
  }

  getById(id: string): Observable<RockModel> {
    return this.http.get<RockModel>(`${this.nest}/${id}`);
  }

  create(request: CreateRockRequestModel): Observable<RockModel[]> {
    return this.http.post<RockModel[]>(this.nest, request);
  }

  update(id: string, changes: Partial<RockModel>): Observable<RockModel> {
    return this.http.patch<RockModel>(`${this.nest}/${id}`, changes);
  }

  delete(id: string): Observable<void> {
    return this.http.patch<void>(`${this.nest}/${id}`, { deleted: true });
  }

  private buildListParams(o: RockListParams): HttpParams {
    let params = new HttpParams();
    if (o.teamId) {
      params = params.set('teamId', o.teamId);
    }
    params = params
      .set('sortField', 'statusCode')
      .set('archived', o.showArchived.toString())
      .set('filterFutureCurrent', o.filterFutureCurrent.toString())
      .set('pageIndex', o.pageIndex.toString())
      .set('pageSize', o.pageSize.toString());

    if (o.rockStatus && o.rockStatus !== 'All') {
      params = params.set('statusCode', o.rockStatus);
    }

    if (o.sortDirection) {
      params = params.set('sortDirection', o.sortDirection);
    }

    if (o.userId !== 'All') {
      params = params.set('userId', o.userId || o.currentUserId);
    }

    return params;
  }
}
